"use client";

import { PREF_KEY, setUserNotificationPreference } from "@/app/utils/notificationpreference";
import { NotificationPermissionState } from "@/app/enums/notification-permission";

export const CLEVERTAP_SW_PATH = "/clevertap-sw.js";
export const PUSH_PROMPT_SESSION_KEY = "clevertap_push_prompted_this_session";
export const PUSH_PROMPT_LAST_SHOWN_AT_KEY = "clevertap_push_prompt_last_shown_at";
/** Production-safe re-prompt interval (24h). */
export const CLEVERTAP_PUSH_ASK_AGAIN_SECONDS = 86400;

const PUSH_PROMPT_COOLDOWN_MS = 24 * 60 * 60 * 1000;

let pushRegistrationInFlight = false;

function isIOSLikeDevice(): boolean {
    if (typeof navigator === "undefined") return false;
    return /iPad|iPhone|iPod|Mac|Macintosh|Darwin/i.test(navigator.userAgent);
}

export function canAutoPromptFromLogin(): boolean {
    return !isIOSLikeDevice();
}

export function getBrowserNotificationPermission(): NotificationPermission | null {
    if (typeof window === "undefined" || !("Notification" in window)) return null;
    return Notification.permission;
}

export function syncNotificationPreferenceWithBrowser(): NotificationPermission | null {
    const permission = getBrowserNotificationPermission();
    if (!permission) return null;
    setUserNotificationPreference(permission);
    return permission;
}

export function shouldShowPushPromptNow(): boolean {
    if (typeof window === "undefined" || !("Notification" in window)) return false;
    if (Notification.permission !== NotificationPermissionState.DEFAULT) return false;
    if (sessionStorage.getItem(PUSH_PROMPT_SESSION_KEY) === "true") return false;

    const lastShownAtRaw = localStorage.getItem(PUSH_PROMPT_LAST_SHOWN_AT_KEY);
    const lastShownAt = lastShownAtRaw ? Number(lastShownAtRaw) : 0;
    if (Number.isFinite(lastShownAt) && Date.now() - lastShownAt < PUSH_PROMPT_COOLDOWN_MS) {
        return false;
    }

    return true;
}

export function markPushPromptShown(): void {
    if (typeof window === "undefined") return;
    sessionStorage.setItem(PUSH_PROMPT_SESSION_KEY, "true");
    localStorage.setItem(PUSH_PROMPT_LAST_SHOWN_AT_KEY, String(Date.now()));
}

export function tryAcquirePushRegistrationLock(): boolean {
    if (pushRegistrationInFlight) return false;
    pushRegistrationInFlight = true;
    return true;
}

export function releasePushRegistrationLock(): void {
    pushRegistrationInFlight = false;
}

function findCleverTapRegistration(registrations: readonly ServiceWorkerRegistration[]): ServiceWorkerRegistration | null {
    for (const registration of registrations) {
        const scriptUrl =
            registration.active?.scriptURL ??
            registration.installing?.scriptURL ??
            registration.waiting?.scriptURL ??
            "";
        if (scriptUrl.includes("clevertap-sw")) {
            return registration;
        }
    }
    return null;
}

export async function getCleverTapServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;

    const registrations = await navigator.serviceWorker.getRegistrations();
    const existing = findCleverTapRegistration(registrations);
    if (existing) return existing;

    if (process.env.NEXT_PUBLIC_CLEVERTAP_PUSH_ENABLED !== "true") return null;

    try {
        return await navigator.serviceWorker.register(CLEVERTAP_SW_PATH, { scope: "/" });
    } catch (error) {
        console.error("CleverTap service worker registration failed:", error);
        return null;
    }
}

export async function logPushDebugState(label: string, identity?: string | number | null): Promise<void> {
    if (process.env.NODE_ENV !== "development" || typeof window === "undefined") return;

    const permission = getBrowserNotificationPermission();
    const registrations = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistrations() : [];
    const ctRegistration = await getCleverTapServiceWorkerRegistration();
    const subscription = ctRegistration ? await ctRegistration.pushManager.getSubscription() : null;

    console.log("[PushDebug]", label, {
        identity: identity ?? null,
        permission,
        serviceWorkers: registrations.map((r) => r.scope),
        clevertapSwActive: ctRegistration?.active?.scriptURL ?? null,
        hasPushSubscription: Boolean(subscription),
        pushRegistrationInFlight,
        sessionPrompted: sessionStorage.getItem(PUSH_PROMPT_SESSION_KEY),
    });
}

/** Once per tab session — avoids showing the blocked dialog on every full-page navigation (e.g. Incognito). */
export const NOTIFICATION_BLOCKED_DIALOG_DISMISSED_SESSION_KEY =
    "notification_blocked_dialog_dismissed_session";

export function hasDismissedNotificationBlockedDialogThisSession(): boolean {
    if (typeof window === "undefined") return false;
    try {
        return sessionStorage.getItem(NOTIFICATION_BLOCKED_DIALOG_DISMISSED_SESSION_KEY) === "true";
    } catch {
        return false;
    }
}

export function markNotificationBlockedDialogDismissedThisSession(): void {
    if (typeof window === "undefined") return;
    try {
        sessionStorage.setItem(NOTIFICATION_BLOCKED_DIALOG_DISMISSED_SESSION_KEY, "true");
    } catch {
        /* ignore quota / privacy mode */
    }
}

export function shouldOpenBlockedNotificationDialog(): boolean {
    if (typeof window === "undefined") return false;
    if (!("Notification" in window)) return true;

    const pref = localStorage.getItem(PREF_KEY);
    return (
        Notification.permission === NotificationPermissionState.DENIED ||
        pref === NotificationPermissionState.DENIED
    );
}
